// Plan A treemap data for the side panel's distribution-of-power block.
// data/plan-a/powerDistribution.json is GENERATED from the model sheet's
// `power-distribution` tab (§2.3 concentration model: world/region stocks,
// global ranks 1-20 with per-column region + name, ranks-21+ tails) by
// `npm run convert-split-plana` — see
// scripts/planA/applySheetUpdates/powerDistributionJson.ts. This replaces the
// May-2026 playground pipeline (company_compute_anchors.json + compute-flow
// cumsum), whose lab series and regional totals had drifted far from the
// model; the compute a cell shows here is exactly the tab's number.

import planAPowerJson from "@/data/plan-a/powerDistribution.json"
import {
  markFrontierByRegionCounts,
  type PowerLabData,
  type PowerLabId,
  type PowerRegionData,
  type PowerRegionId,
} from "../../../../power-bubble-chart/powerBubbleChartData"
import { tailWeights, REGION_TO_BUBBLE } from "./companyAnchorsToPowerRegions"

type PlanARegion = "US" | "CH" | "RoW"
type PlanAPowerData = {
  dates: number[]
  regionStocks: Record<PlanARegion, number[]>
  ranks: { id: string; series: number[]; region: PlanARegion[]; name: string[] }[]
  tails: Record<PlanARegion, { series: number[]; count: number[]; sigma: number[] }>
}

export const PLAN_A_POWER = planAPowerJson as unknown as PlanAPowerData

const M_TO_RAW = 1e6
// Same visual convention as the anchors/branch builders: one collapsed
// "Rest of X" cell until the tail splits into individual companies.
const TAIL_SPLIT_FROM = 2030.67
// The tab's tails cover ranks 21-200; render only the largest few as cells
// (the tooltip lists every rendered cell, so ~87 synthetic CH companies must
// not become ~87 hover rows) and keep the remainder in the "Rest of X"
// aggregate so a region's cells still sum exactly to its stock.
const TAIL_CELLS_SHOWN = 12

// Sheet holder name → app lab id (drives logos via labIconById) + display
// name. Ranks the sheet leaves anonymous render as "US co. N" per their
// size order within the region.
const SHEET_NAME_TO_LAB: Record<string, { id: string; name: string }> = {
  OpenAI: { id: "openai", name: "OpenAI" },
  GDM: { id: "google-deepmind", name: "Google DeepMind" },
  Anthropic: { id: "anthropic", name: "Anthropic" },
  Meta: { id: "meta", name: "Meta AI" },
  SpaceX: { id: "spacex", name: "SpaceX" },
}

const REGION_CO_PREFIX: Record<PlanARegion, string> = {
  US: "US",
  CH: "CN",
  RoW: "RoW",
}

// Latest tab column at or before the stepped HUD date (ctx.powerColumnDate:
// Jan-1 of the story year, or a .0/.33/.67 trimester through 2029-2030),
// clamped to the tab's horizon. The .005 tolerance absorbs the header's
// two-decimal columns (2029.33) vs the exact thirds the HUD steps on.
export function columnIdxAtOrBefore(dates: number[], d: number): number {
  let idx = 0
  for (let i = 0; i < dates.length; i++) {
    if (dates[i] <= d + 0.005) idx = i
    else break
  }
  return idx
}

// Regions for the Plan A treemap at the HUD's stepped column date — every
// cell's compute is the power-distribution tab's own number for that column.
export function getPlanAPowerRegions(columnDate: number): PowerRegionData[] {
  const data = PLAN_A_POWER
  const idx = columnIdxAtOrBefore(data.dates, columnDate)
  const d = data.dates[idx]
  return (Object.keys(REGION_TO_BUBBLE) as PlanARegion[]).map((r) => {
    const def = REGION_TO_BUBBLE[r]
    const prefix = REGION_CO_PREFIX[r]
    const rankCells: PowerLabData[] = data.ranks
      .filter((rank) => rank.region[idx] === r && (rank.series[idx] ?? 0) > 0)
      .map((rank, positionInRegion) => {
        const named = SHEET_NAME_TO_LAB[rank.name[idx]]
        return {
          id: (named?.id ?? rank.id) as PowerLabId,
          name: named?.name ?? `${prefix} co. ${positionInRegion + 1}`,
          regionId: def.id,
          compute: (rank.series[idx] ?? 0) * M_TO_RAW,
        }
      })
    const regionalTotal = (data.regionStocks[r][idx] ?? 0) * M_TO_RAW
    const rankTotal = rankCells.reduce((sum, lab) => sum + lab.compute, 0)
    const tailTotal = Math.max(0, regionalTotal - rankTotal)
    const tailCells = buildTailCells(r, def.id, tailTotal, idx, d, rankCells.length)
    return {
      id: def.id,
      name: def.name,
      shortName: def.shortName,
      computeStock: regionalTotal,
      labs: markFrontierByRegionCounts([...rankCells, ...tailCells], r, d),
    }
  })
}

// Ranks 21+ as cells: collapsed into the "Rest of X" aggregate before the
// split date, then the largest TAIL_CELLS_SHOWN of a lognormal synthesis
// (company count + sigma from the tab, matching the §2.3 model's actual
// rank-21+ distribution) with the remainder staying in the aggregate.
function buildTailCells(
  region: PlanARegion,
  regionId: PowerRegionId,
  tailTotal: number,
  idx: number,
  d: number,
  regionRankCount: number,
): PowerLabData[] {
  if (tailTotal <= 0) return []
  const restCell: PowerLabData = {
    id: `${region}-tail` as PowerLabId,
    name: region === "US" ? "Rest of US" : region === "CH" ? "Rest of China" : "Rest of World",
    regionId,
    compute: tailTotal,
  }
  if (d < TAIL_SPLIT_FROM) return [restCell]
  const { count, sigma } = PLAN_A_POWER.tails[region]
  const companyCount = Math.max(0, Math.round(count[idx] ?? 0))
  // Same seeds as the branch builder so tail-company identities stay stable.
  const seed = 31 + (region === "US" ? 0 : region === "CH" ? 100 : 200)
  const weights = tailWeights(companyCount, "lognormal", sigma[idx] ?? 1, seed)
  const shown = weights.slice(0, TAIL_CELLS_SHOWN)
  const shownTotal = shown.reduce((sum, w) => sum + w, 0)
  const cells = shown.map((weight, i) => ({
    id: `${region}-tail-${i}` as PowerLabId,
    name: `${REGION_CO_PREFIX[region]} co. ${regionRankCount + 1 + i}`,
    regionId,
    compute: tailTotal * weight,
  }))
  const remainder = tailTotal * Math.max(0, 1 - shownTotal)
  return remainder > 0 ? [...cells, { ...restCell, compute: remainder }] : cells
}
